// src/components/Calendar/CalendarVoteBox.jsx
import {
    Box,
    Typography,
    Button,
    LinearProgress,
    FormControlLabel,
    Radio,
    Checkbox,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Chip,
    List,
    ListItem,
    ListItemText,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import { styled } from '@mui/material/styles';
import { useState, useMemo } from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import api from '../../api/axios';
import { useUser } from '../../contexts/UserContext';

dayjs.extend(relativeTime);

const ExpandBtn = styled(IconButton, {
    shouldForwardProp: (prop) => prop !== 'expand',
})(({ expand }) => ({
    transform: expand ? 'rotate(180deg)' : 'rotate(0deg)',
    transition: 'transform 0.2s',
    padding: 0,
    width: 28,
    height: 28,
    flex: '0 0 28px',
}));

const getDeadlineText = (deadline) => {
    const now = dayjs();
    const end = dayjs(deadline);

    if (now.isAfter(end)) return '마감된 투표입니다.';
    const diffHours = end.diff(now, 'hour');
    const diffDays = end.diff(now, 'day');

    if (diffDays >= 1) {
        return `투표가 ${diffDays}일 후에 종료됩니다.`;
    } else if (diffHours > 0) {
        return `투표가 ${diffHours}시간 후에 종료됩니다.`;
    } else {
        return '투표가 곧 종료됩니다.';
    }
};

export default function CalendarVoteBox({
    postId,
    voteData,
    hasVoted,
    onVoteSuccess,
    isAnonymousVote = true,
    allowMultiple = false,
}) {
    console.log('🔍 voteData:', voteData);
    const { userInfo } = useUser();
    const isAdmin = ['admin', 'master'].includes(userInfo?.role);
    const isAnonymous = voteData.is_anonymous_vote ?? voteData.is_anonymous ?? false;
    const showVoterModal = isAdmin || (!isAnonymous && !isAnonymousVote);

    const optionIds = voteData.vote_option_ids || [];
    const [selected, setSelected] = useState(allowMultiple ? [] : '');
    const [results, setResults] = useState(voteData.vote_result || []);
    const [voted, setVoted] = useState(hasVoted || false);
    const [loading, setLoading] = useState(false);

    const [modalOpen, setModalOpen] = useState(false);
    const [modalOpt, setModalOpt] = useState('');
    const [modalUsers, setModalUsers] = useState([]);

    const rawDeadline = voteData.vote_deadline ?? voteData.deadline;
    const deadlinePassed = dayjs().isAfter(dayjs(rawDeadline));
    const showResult = voted || deadlinePassed;

    const optionToVoters = useMemo(() => {
        const map = {};
        const ids = voteData.vote_option_ids || [];
        const texts = voteData.vote_options || [];

        ids.forEach((id, i) => {
            map[id] = { text: texts[i], voters: [] };
        });

        voteData.voters?.forEach((v) => {
            const selected = Array.isArray(v.selected_option_ids)
                ? v.selected_option_ids
                : [];
            selected.forEach((id) => {
                if (map[id]) {
                    map[id].voters.push(v);
                }
            });
        });

        return map;
    }, [voteData]);

    const handleVote = () => {
        if (
            selected == null ||
            (Array.isArray(selected) && selected.length === 0)
        ) {
            alert('선택지를 선택해주세요.');
            return;
        }

        const ids = Array.isArray(selected)
            ? selected.map((i) => optionIds[i])
            : [optionIds[selected]];
        setLoading(true);
        api
            .post(`/api/schedules/${postId}/vote/`, { selected_options: ids })
            .then(({ data }) => {
                const { vote, schedule } = data;
                setVoted(true);
                setResults(vote.vote_result);
                onVoteSuccess?.(schedule);
            })
            .catch(() => alert('투표 실패'))
            .finally(() => setLoading(false));
    };

    const totalVotes = results.reduce((a, b) => a + b, 0) || 0;
    const isChecked = (i) =>
        allowMultiple ? selected.includes(i) : selected === i;
    const handleChange = (i) =>
        allowMultiple
            ? setSelected((p) =>
                p.includes(i) ? p.filter((x) => x !== i) : [...p, i]
            )
            : setSelected(i);

    const openModal = (optionId) => {
        const entry = optionToVoters[optionId] || { text: '', voters: [] };
        setModalOpt(entry.text);
        setModalUsers(entry.voters);
        setModalOpen(true);
    };

    return (
        <>
            <Box
                sx={{
                    mt: 4,
                    p: 2,
                    border: '1px solid #ddd',
                    borderRadius: 2,
                    textAlign: 'left',
                }}
            >
                <Typography variant="h6" gutterBottom>
                    {voteData.question}
                    {allowMultiple && (
                        <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 1 }}
                        >
                            (복수선택가능)
                        </Typography>
                    )}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    🕒 {getDeadlineText(rawDeadline)}
                </Typography>

                {/* 익명 안내 표시 */}
                {isAnonymous && (
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                        👤 익명 투표입니다.
                    </Typography>
                )}

                {showResult ? (
                    <Box>
                        {voteData.vote_options.map((opt, idx) => {
                            const count = results[idx] || 0;
                            const percent = ((count / (totalVotes || 1)) * 100).toFixed(1);
                            const myPick =
                                voted &&
                                (Array.isArray(selected)
                                    ? selected.includes(idx)
                                    : selected === idx);

                            return (
                                <Box key={idx} sx={{ mb: 2 }}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        {myPick && <CheckIcon fontSize="small" color="primary" />}
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                flexGrow: 1,
                                                minWidth: 0,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}
                                        >
                                            {opt}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ mr: 0.5 }}
                                        >
                                            {count}명
                                        </Typography>
                                        {showVoterModal && (
                                            <ExpandBtn onClick={() => openModal(optionIds[idx])}>
                                                <ExpandMoreIcon fontSize="small" />
                                            </ExpandBtn>
                                        )}
                                    </Stack>
                                    <LinearProgress
                                        variant="determinate"
                                        value={+percent}
                                        sx={{
                                            mt: 0.5,
                                            height: 8,
                                            borderRadius: 4,
                                            backgroundColor: '#e0e0e0',
                                            '& .MuiLinearProgress-bar': {
                                                backgroundColor: myPick ? 'primary.main' : 'warning.main',
                                            },
                                        }}
                                    />
                                </Box>
                            );
                        })}
                        <Typography variant="caption" color="text.secondary">
                            ✔️ {voteData.voter_count ?? voteData.voters?.length ?? 0}명 참여
                        </Typography>

                        {voted && !deadlinePassed && (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                    setVoted(false);
                                    setSelected(allowMultiple ? [] : '');
                                }}
                                sx={{ mt: 2 }}
                            >
                                투표 변경하기
                            </Button>
                        )}
                    </Box>
                ) : (
                    <Box>
                        <Stack spacing={1}>
                            {voteData.vote_options.map((opt, idx) => (
                                <FormControlLabel
                                    key={idx}
                                    control={
                                        allowMultiple ? (
                                            <Checkbox
                                                checked={isChecked(idx)}
                                                onChange={() => handleChange(idx)}
                                            />
                                        ) : (
                                            <Radio
                                                checked={isChecked(idx)}
                                                onChange={() => handleChange(idx)}
                                            />
                                        )
                                    }
                                    label={opt}
                                />
                            ))}
                        </Stack>

                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 2 }}
                        >
                            ✔️ {voteData.voter_count ?? voteData.voters?.length ?? 0}명 참여
                        </Typography>

                        {!deadlinePassed && (
                            <Button
                                variant="contained"
                                sx={{ mt: 2 }}
                                onClick={handleVote}
                                disabled={loading}
                            >
                                투표하기
                            </Button>
                        )}
                    </Box>
                )}
            </Box>

            <Dialog open={modalOpen} onClose={() => setModalOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 600 }}>{modalOpt}</DialogTitle>
                <DialogContent dividers>
                    {modalUsers.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            참여자 없음
                        </Typography>
                    ) : (
                        <List dense>
                            {modalUsers.map((user, i) => (
                                <ListItem key={i} disableGutters>
                                    <ListItemText
                                        primary={
                                            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                                {user.sub_title && (
                                                    <Chip
                                                        label={user.sub_title}
                                                        size="small"
                                                        variant="outlined"
                                                        color="secondary"
                                                        sx={{ fontSize: '0.75rem', height: 22 }}
                                                    />
                                                )}
                                                {user.title && (
                                                    <Chip
                                                        label={user.title}
                                                        size="small"
                                                        variant="outlined"
                                                        color="primary"
                                                        sx={{ fontSize: '0.75rem', height: 22 }}
                                                    />
                                                )}
                                                <Typography component="span" fontWeight={700}>
                                                    {user.nickname}
                                                </Typography>
                                                {user.emoticon && (
                                                    <Typography component="span" fontSize="1.4rem">
                                                        {user.emoticon}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        }
                                    />
                                </ListItem>
                            ))}
                        </List>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}